import {
  buildTriToPolyMap,
  getAdjacencyCache,
  vertexNeighbors,
} from "../modeling/topology.ts";
import type { PolygonTopology } from "../modeling/types.ts";

const triToPolyCache = new WeakMap<PolygonTopology, Uint32Array>();
const identityIndexCache = new Map<number, Uint32Array>();
const nearestDistanceCache = new WeakMap<
  PolygonTopology,
  WeakMap<Float32Array, Float32Array>
>();

export function getCachedTriToPolyMap(topology: PolygonTopology): Uint32Array {
  let map = triToPolyCache.get(topology);
  if (!map) {
    map = buildTriToPolyMap(topology);
    triToPolyCache.set(topology, map);
  }
  return map;
}

export function getIdentityVertexIndices(vertexCount: number): Uint32Array {
  let indices = identityIndexCache.get(vertexCount);
  if (!indices) {
    indices = new Uint32Array(vertexCount);
    for (let i = 0; i < vertexCount; i++) indices[i] = i;
    identityIndexCache.set(vertexCount, indices);
  }
  return indices;
}

export function getNearestVertexDistances(
  topology: PolygonTopology,
  vertices: Float32Array,
): Float32Array {
  let byVertices = nearestDistanceCache.get(topology);
  if (!byVertices) {
    byVertices = new WeakMap<Float32Array, Float32Array>();
    nearestDistanceCache.set(topology, byVertices);
  }
  let dists = byVertices.get(vertices);
  if (dists) return dists;

  const vertexCount = vertices.length / 3;
  const adjCache = getAdjacencyCache(topology, vertexCount);
  dists = new Float32Array(vertexCount);
  for (let vi = 0; vi < vertexCount; vi++) {
    const ax = vertices[vi * 3]!;
    const ay = vertices[vi * 3 + 1]!;
    const az = vertices[vi * 3 + 2]!;
    let best = Infinity;
    const neighbors = vertexNeighbors(adjCache, topology, vi);
    for (const ni of neighbors) {
      const dx = vertices[ni * 3]! - ax;
      const dy = vertices[ni * 3 + 1]! - ay;
      const dz = vertices[ni * 3 + 2]! - az;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 > 1e-12 && d2 < best) best = d2;
    }
    dists[vi] = best < Infinity ? Math.sqrt(best) : 1;
  }

  byVertices.set(vertices, dists);
  return dists;
}
